import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  SampleMasterFormModal,
  ServiceMasterViewModal,
  ConfirmDialog,
  ModuleHeader
} from 'components/modals';
import type {
  ActionButton,
  ViewModeOption,
  ServiceMasterRecord
} from 'components/modals';
import { ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect, SyutSearchGrid, MinimizedModalStrip } from 'components/ui';
import notificationService from '../../../../services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useSampleServices } from 'hooks/api/useSampleServices';
import { sampleServiceService, sampleCategoryService, statusService } from 'api/services';
import type { SampleServiceQueryParams } from 'api/types';

/**
 * ============================================================================
 * SAMPLE MASTER SCREEN — REFERENCE IMPLEMENTATION
 * ============================================================================
 * Demonstrates:
 *   - Server-side pagination via SyutGrid + useSampleServices hook
 *   - Add / Edit / Copy / Delete via SampleMasterFormModal
 *   - View details via ServiceMasterViewModal
 *   - Minimize-to-bar (all form & view modals)
 *   - Column visibility control
 *   - Async category filter via SyutSelect
 *   - Grid & Card (grid) view modes
 * ============================================================================
 */

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

interface ColumnDefinition {
  id: string;
  label: string;
  field: string;
  required: boolean;
  defaultVisible: boolean;
  width?: number;
  sortable?: boolean;
}

const columnDefinitions: ColumnDefinition[] = [
  { id: 'select',      label: '',             field: 'select',      required: true,  defaultVisible: true,  width: 50, sortable: false },
  { id: 'code',        label: 'Code',         field: 'code',        required: false, defaultVisible: true,  sortable: true },
  { id: 'serviceName', label: 'Service Name', field: 'serviceName', required: false, defaultVisible: true,  sortable: true },
  { id: 'description', label: 'Description',  field: 'description', required: false, defaultVisible: true,  sortable: false },
  { id: 'categoryName',label: 'Category',     field: 'categoryName',required: false, defaultVisible: true,  sortable: true },
  { id: 'uom',         label: 'UOM',          field: 'uom',         required: false, defaultVisible: true,  sortable: true },
  { id: 'scope',       label: 'Scope',        field: 'scope',       required: false, defaultVisible: true,  sortable: true },
  { id: 'rate',        label: 'Rate',         field: 'rate',        required: false, defaultVisible: false, sortable: true },
  { id: 'status',      label: 'Status',       field: 'statusCode',  required: false, defaultVisible: true,  sortable: true },
  { id: 'effectiveFrom',label:'Eff. From',    field: 'effectiveFrom',required: false,defaultVisible: false, sortable: true },
  { id: 'effectiveTo', label: 'Eff. To',      field: 'effectiveTo', required: false, defaultVisible: false, sortable: true },
  { id: 'actions',     label: 'Actions',      field: 'actions',     required: true,  defaultVisible: true,  width: 80, sortable: false },
];

// ============================================================================
// COLUMN SELECTOR COMPONENT
// ============================================================================

interface ColumnSelectorProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
  onReset: () => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns: initialVisible,
  onApply,
  onReset
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialVisible);
  const [isMobile, setIsMobile] = useState(false);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedColumns(initialVisible);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, initialVisible]);

  const toggleColumn = (columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column?.required) return;
    setSelectedColumns(prev =>
      prev.includes(columnId) ? prev.filter(id => id !== columnId) : [...prev, columnId]
    );
  };

  const handleSelectAll = () => {
    const allIds = columns.filter(col => !col.required).map(col => col.id);
    const requiredIds = columns.filter(col => col.required).map(col => col.id);
    if (selectedColumns.length === columns.length) setSelectedColumns(requiredIds);
    else setSelectedColumns([...requiredIds, ...allIds]);
  };

  const handleApply = () => { onApply(selectedColumns); setIsOpen(false); };
  const handleCancel = () => { setSelectedColumns(initialVisible); setIsOpen(false); };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={panelRef}>
      <button
        className={`btn-icon ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Manage Columns"
        style={isOpen ? { background: 'var(--accent-gradient)', color: 'white', borderColor: 'var(--primary)' } : {}}
      >
        <IconSystem.grid size={18} />
      </button>

      {isOpen && (
        <>
          {isMobile && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999 }}
              onClick={() => setIsOpen(false)} />
          )}
          <div style={{
            position: isMobile ? 'fixed' : 'absolute',
            top: isMobile ? '10%' : 'calc(100% + 0.5rem)',
            left: isMobile ? '50%' : 'auto',
            right: isMobile ? 'auto' : 0,
            transform: isMobile ? 'translateX(-50%)' : 'none',
            width: isMobile ? 'calc(100vw - 2rem)' : '280px',
            maxWidth: '280px', maxHeight: '450px',
            background: 'var(--bg-primary)', border: '2px solid var(--primary)',
            borderRadius: '0.75rem', boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
            zIndex: 1000, display: 'flex', flexDirection: 'column', overflow: 'hidden'
          }}>
            <div style={{ padding: '0.75rem 1rem', borderBottom: '2px solid var(--border-color)', background: 'var(--bg-secondary)', borderRadius: '0.75rem 0.75rem 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Manage Columns</h3>
              <button onClick={() => setIsOpen(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <IconSystem.x size={16} />
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem 0', minHeight: 0 }}>
              {columns.map(column => (
                <div
                  key={column.id}
                  onClick={() => toggleColumn(column.id)}
                  style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 1rem', cursor: column.required ? 'not-allowed' : 'pointer', gap: '0.625rem', opacity: column.required ? 0.6 : 1 }}
                >
                  <div style={{ width: '1rem', height: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid var(--border-color)', borderRadius: '0.25rem', flexShrink: 0, ...(selectedColumns.includes(column.id) ? { background: 'var(--accent-gradient)', borderColor: 'var(--primary)', color: 'white' } : {}) }}>
                    {selectedColumns.includes(column.id) && <IconSystem.check size={12} />}
                  </div>
                  <span style={{ flex: 1, fontSize: '0.85rem', color: 'var(--text-primary)', userSelect: 'none' }}>
                    {column.label}{column.required && ' *'}
                  </span>
                </div>
              ))}
            </div>
            <div style={{ padding: '0.75rem', borderTop: '2px solid var(--border-color)', flexShrink: 0 }}>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <button onClick={handleSelectAll} style={{ flex: 1, padding: '0.375rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.375rem', cursor: 'pointer' }}>
                  {selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}
                </button>
                <button onClick={() => setConfirmResetOpen(true)} style={{ flex: 1, padding: '0.375rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.375rem', cursor: 'pointer' }}>
                  Reset
                </button>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button onClick={handleCancel} className="btn btn-secondary">Cancel</button>
                <button onClick={handleApply} className="btn btn-primary">Apply</button>
              </div>
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmResetOpen}
        title="Reset Columns"
        message="Reset to default column configuration?"
        confirmLabel="Reset"
        cancelLabel="Cancel"
        onConfirm={() => { onReset(); setConfirmResetOpen(false); setIsOpen(false); }}
        onCancel={() => setConfirmResetOpen(false)}
      />
    </div>
  );
};

// ============================================================================
// SCREEN PROPS
// ============================================================================

/** SyutGrid requires id: string — safe cast since API always returns UUID strings */
type GridRecord = Omit<ServiceMasterRecord, 'id'> & { id: string };

interface SampleMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

type ViewMode = 'list' | 'grid' | 'calendar';

// ============================================================================
// MAIN SCREEN
// ============================================================================

const SampleMasterScreen: React.FC<SampleMasterScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter(col => col.defaultVisible).map(col => col.id)
  );

  // Server-side pagination & search state
  const [queryParams, setQueryParams] = useState<SampleServiceQueryParams>({ page: 1, pageSize: 25 });
  const { data: apiData, loading, pagination: paginationMeta, refetch } = useSampleServices(queryParams);

  // Search debounce
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter panel
  const [filterOpen, setFilterOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [scopeFilter, setScopeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [effectiveFromFilter, setEffectiveFromFilter] = useState('');
  const [effectiveToFilter, setEffectiveToFilter] = useState('');

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchPopupOpen, setSearchPopupOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<ServiceMasterRecord | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const copyInstanceCounter = useRef(0);
  const [copyInstanceId, setCopyInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  // Column preferences persistence
  useEffect(() => {
    const saved = localStorage.getItem('sample-master-columns');
    if (saved) {
      try { setVisibleColumns(JSON.parse(saved)); } catch { /* ignore */ }
    }
  }, []);

  const handleApplyColumns = useCallback((cols: string[]) => {
    setVisibleColumns(cols);
    localStorage.setItem('sample-master-columns', JSON.stringify(cols));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaults = columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
    setVisibleColumns(defaults);
    localStorage.removeItem('sample-master-columns');
  }, []);

  // Pagination handlers
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // Restore handlers (minimize support)
  const handleRestoreModal = useCallback((restoredMode: 'create' | 'edit' | 'copy', instanceId?: string, serviceCode?: string) => {
    if (instanceId) {
      if (restoredMode === 'copy') setCopyInstanceId(instanceId);
      else setCreateInstanceId(instanceId);
    }
    if (serviceCode) {
      const rec = apiData.find(s => s.code === serviceCode);
      if (rec) setSelectedRecord(rec);
    }
    setFormMode(restoredMode);
    setFormModalOpen(true);
  }, [apiData]);

  const handleRestoreView = useCallback((serviceCode: string) => {
    const rec = apiData.find(s => s.code === serviceCode);
    if (rec) { setSelectedRecord(rec); setViewModalOpen(true); }
  }, [apiData]);

  // F2 shortcut for search popup
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2' && !searchPopupOpen && !formModalOpen && !viewModalOpen && !deleteModalOpen) {
        e.preventDefault();
        setSearchPopupOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchPopupOpen, formModalOpen, viewModalOpen, deleteModalOpen]);

  // Actions
  const handleView = useCallback((id: string) => {
    const record = apiData.find(s => s.id === id);
    if (!record) return;
    const viewId = tabId ? `${tabId}-view-modal-${record.code}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRecord(record);
    setViewModalOpen(true);
  }, [apiData, tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleEdit = useCallback((id: string) => {
    const record = apiData.find(s => s.id === id);
    if (!record) return;
    const modalId = tabId ? `${tabId}-form-modal-edit-${record.code}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setFormMode('edit');
    setSelectedRecord(record);
    setFormModalOpen(true);
  }, [apiData, tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleCopy = useCallback((id: string) => {
    const record = apiData.find(s => s.id === id);
    if (!record) return;
    if (formModalOpen) formModalMinimizeRef.current?.();
    const newId = String(++copyInstanceCounter.current);
    setCopyInstanceId(newId);
    setFormMode('copy');
    setSelectedRecord(record);
    setFormModalOpen(true);
  }, [apiData, formModalOpen]);

  const handleDelete = useCallback((id: string) => {
    const record = apiData.find(s => s.id === id);
    setSelectedRecord(record || null);
    setDeleteModalOpen(true);
  }, [apiData]);

  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setFormMode('create');
    setSelectedRecord(null);
    setFormModalOpen(true);
  }, []);

  const handleSaveSuccess = useCallback(() => {
    setFormModalOpen(false);
    setSelectedRecord(null);
    refetch();
  }, [refetch]);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedRecord) return;
    setIsDeleting(true);
    try {
      await sampleServiceService.delete(selectedRecord.id as string);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success(`"${selectedRecord.serviceName}" has been deleted.`);
      setSelectedRecord(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      notificationService.toast.error(err?.response?.data?.message || 'An error occurred while deleting.');
    }
  }, [selectedRecord, refetch]);

  // Grid columns
  const tableColumns: SyutGridColumn<GridRecord>[] = useMemo(() => [
    { id: 'code',         header: 'Code',         accessorKey: 'code',         size: 120 },
    { id: 'serviceName',  header: 'Service Name',  accessorKey: 'serviceName',  size: 200 },
    { id: 'description',  header: 'Description',   accessorKey: 'description',  size: 200, enableSorting: false },
    { id: 'categoryName', header: 'Category',      accessorKey: 'categoryName', size: 160 },
    { id: 'uom',          header: 'UOM',           accessorKey: 'uom',          size: 90 },
    { id: 'scope',        header: 'Scope',         accessorKey: 'scope',        size: 110 },
    {
      id: 'rate', header: 'Rate', accessorKey: 'rate', size: 100,
      cell: ({ getValue }) => {
        const v = getValue() as number | null;
        return v !== null && v !== undefined ? v.toLocaleString() : '-';
      },
    },
    {
      id: 'status', header: 'Status', accessorKey: 'statusName', size: 110,
      cell: ({ row }) => {
        const rec = row.original as ServiceMasterRecord;
        return (
          <span
            className="badge"
            style={rec.statusColor ? { background: rec.statusColor, color: '#fff' } : undefined}
          >
            {rec.statusName || rec.statusCode || '—'}
          </span>
        );
      },
    },
    { id: 'effectiveFrom', header: 'Eff. From', accessorKey: 'effectiveFrom', size: 120 },
    {
      id: 'effectiveTo', header: 'Eff. To', accessorKey: 'effectiveTo', size: 120,
      cell: ({ getValue }) => (getValue() as string) || '-',
    },
  ], []);

  const getRowActions = useCallback((record: GridRecord): ActionMenuItem[] => [
    { id: 'view',   label: 'View Details', icon: <IconSystem.eye   size={18} />, onClick: () => handleView(record.id as string) },
    { id: 'edit',   label: 'Edit',         icon: <IconSystem.edit  size={18} />, onClick: () => handleEdit(record.id as string) },
    { id: 'copy',   label: 'Copy',         icon: <IconSystem.copy  size={18} />, onClick: () => handleCopy(record.id as string) },
    { id: 'delete', label: 'Delete',       icon: <IconSystem.trash size={18} />, onClick: () => handleDelete(record.id as string), variant: 'danger' },
  ], [handleView, handleEdit, handleCopy, handleDelete]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list',     icon: <IconSystem.list     size={16} />, label: 'List View',     tooltip: 'List View' },
    { id: 'grid',     icon: <IconSystem.grid     size={16} />, label: 'Grid View',     tooltip: 'Grid View' },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  const actionButtons: ActionButton[] = [
    { id: 'search',  icon: <IconSystem.search  size={18} />, label: 'Search',  onClick: () => setSearchPopupOpen(true), tooltip: 'Search (F2)', variant: 'icon', visible: true },
    { id: 'add',     icon: <IconSystem.add     size={18} />, label: 'Add New', onClick: handleAddNew, tooltip: 'Add New Service', variant: 'icon', visible: true },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: refetch, tooltip: 'Refresh', variant: 'icon' },
    { id: 'export',  icon: <IconSystem.download size={18} />, label: 'Export', onClick: () => console.log('Export'), tooltip: 'Export', variant: 'icon' },
  ];

  // Async category filter options
  const loadCategoryFilterOptions = useCallback(async (inputValue: string) => {
    const res = await sampleCategoryService.getAll({
      searchTerm: inputValue || undefined,
      pageSize: inputValue ? 50 : 200,
    });
    return [
      { value: '', label: 'All Categories' },
      ...res.data.map(c => ({ value: c.id as string, label: `${c.code} - ${c.name}` })),
    ];
  }, []);

  const clearFilters = useCallback(() => {
    setCategoryFilter(''); setScopeFilter(''); setStatusFilter('');
    setEffectiveFromFilter(''); setEffectiveToFilter('');
    setQueryParams(prev => ({ page: 1, pageSize: prev.pageSize }));
  }, []);

  const loadStatusOptions = useCallback(async (inputValue: string) => {
    const statuses = await statusService.getByType('GENERAL');
    const filtered = inputValue
      ? statuses.filter(s => s.statusName.toLowerCase().includes(inputValue.toLowerCase()))
      : statuses;
    return filtered.map(s => ({ value: s.statusCode, label: s.statusName }));
  }, []);

  const applyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      categoryId: categoryFilter || undefined,
      scope: scopeFilter as any || undefined,
      statusCode: statusFilter || undefined,
      includeInactive: statusFilter ? true : undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [categoryFilter, scopeFilter, statusFilter]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Service Master"
        description="Catalogue of all services managed by the enterprise"
        icon={moduleIcon && (IconSystem as any)[moduleIcon]
          ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
          : undefined
        }
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search services...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode as ViewMode) }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          {viewMode === 'list' && (
            <SyutGrid
              data={apiData as unknown as GridRecord[]}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              manualPagination
              totalCount={paginationMeta.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              rowActions={getRowActions}
              emptyMessage={loading ? 'Loading...' : 'No services found'}
              frozenLeftColumns={['select', 'code']}
              frozenRightColumns={['actions']}
              visibleColumns={visibleColumns}
            />
          )}

          {viewMode === 'grid' && (() => {
            const currentPage = queryParams.page ?? 1;
            const currentPageSize = queryParams.pageSize ?? 25;
            const totalPages = Math.max(1, Math.ceil(paginationMeta.totalItems / currentPageSize));
            return (
              <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start' }}>
                  {apiData.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      <IconSystem.grid size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                      <p>{loading ? 'Loading...' : 'No services found'}</p>
                    </div>
                  ) : (
                    (apiData as GridRecord[]).map((record) => (
                      <div key={record.id} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.75rem', padding: '1.25rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <div>
                            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>{record.code}</div>
                            <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0.25rem 0 0' }}>{record.serviceName}</h3>
                          </div>
                          <ActionMenu items={getRowActions(record)} />
                        </div>
                        {record.description && (
                          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{record.description}</p>
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border-color)' }}>
                          <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Category</div><div style={{ fontSize: '0.85rem', fontWeight: 500 }}>{record.categoryName || '—'}</div></div>
                          <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>UOM</div><div style={{ fontSize: '0.85rem', fontWeight: 500 }}>{record.uom || '—'}</div></div>
                          <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Scope</div><div style={{ fontSize: '0.85rem', fontWeight: 500 }}>{record.scope || '—'}</div></div>
                          <div><div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Status</div><span className="badge" style={record.statusColor ? { background: record.statusColor, color: '#fff' } : undefined}>{record.statusName || record.statusCode || '—'}</span></div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {apiData.length > 0 && (
                  <div className="pagination-footer">
                    <div className="pagination-info">Showing {apiData.length} of {paginationMeta.totalItems} items</div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select value={currentPageSize} onChange={(e) => handlePageSizeChange(Number(e.target.value))} style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '0.5rem' }}>
                        {[10, 25, 50, 100].map(size => <option key={size} value={size}>{size} / page</option>)}
                      </select>
                      <button className="page-btn" onClick={() => handlePageChange(0)} disabled={currentPage <= 1}><IconSystem.chevronLeft size={16} /><IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} /></button>
                      <button className="page-btn" onClick={() => handlePageChange(currentPage - 2)} disabled={currentPage <= 1}><IconSystem.chevronLeft size={16} /></button>
                      <span style={{ padding: '0 0.5rem', fontSize: '0.85rem' }}>Page {currentPage} of {totalPages}</span>
                      <button className="page-btn" onClick={() => handlePageChange(currentPage)} disabled={currentPage >= totalPages}><IconSystem.chevronRight size={16} /></button>
                      <button className="page-btn" onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages}><IconSystem.chevronRight size={16} /><IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} /></button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {viewMode === 'calendar' && (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              <IconSystem.calendar size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
              <h3 style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Calendar View Not Applicable</h3>
              <p>Calendar view is not available for Service Master</p>
            </div>
          )}
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <IconSystem.filter size={14} />
                <h3 className="search-panel-title">Filters</h3>
              </div>
              <button onClick={() => { clearFilters(); setFilterOpen(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }} title="Close filters">
                <IconSystem.x size={16} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Category"
                  isAsync
                  loadOptions={loadCategoryFilterOptions}
                  defaultOptions={true}
                  cacheOptions={false}
                  value={categoryFilter}
                  onChange={(val) => setCategoryFilter((val as string) || '')}
                  placeholder="All Categories"
                  isClearable
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Scope"
                  options={[
                    { value: 'Entity', label: 'Entity' },
                    { value: 'Enterprise', label: 'Enterprise' },
                    { value: 'BusinessUnit', label: 'Business Unit' },
                  ]}
                  value={scopeFilter}
                  onChange={(val) => setScopeFilter((val as string) || '')}
                  placeholder="All Scopes"
                  isClearable
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  isAsync
                  loadOptions={loadStatusOptions}
                  defaultOptions={true}
                  cacheOptions={false}
                  value={statusFilter}
                  onChange={(val) => setStatusFilter((val as string) || '')}
                  placeholder="All Status"
                  isClearable
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Effective From</label>
                <input type="date" className="form-input" value={effectiveFromFilter} onChange={(e) => setEffectiveFromFilter(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Effective To</label>
                <input type="date" className="form-input" value={effectiveToFilter} onChange={(e) => setEffectiveToFilter(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      {/* Form Modal */}
      <SampleMasterFormModal
        isOpen={formModalOpen}
        mode={formMode}
        service={selectedRecord}
        onSave={handleSaveSuccess}
        onClose={() => { setFormModalOpen(false); setSelectedRecord(null); }}
        openedBy="SampleMasterScreen"
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        copyInstanceId={copyInstanceId}
        minimizeRef={formModalMinimizeRef}
      />

      {/* View Modal */}
      <ServiceMasterViewModal
        isOpen={viewModalOpen}
        service={selectedRecord}
        onClose={() => { setViewModalOpen(false); setSelectedRecord(null); }}
        onEdit={(record) => handleEdit(record.id as string)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreView={handleRestoreView}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedRecord?.serviceName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
      />

      {/* Search Popup (F2) */}
      <SyutSearchGrid<ServiceMasterRecord>
        isOpen={searchPopupOpen}
        onClose={() => setSearchPopupOpen(false)}
        onSelect={(record) => { setSearchPopupOpen(false); handleView(record.id as string); }}
        title="Search Services"
        columns={[
          { key: 'code',        header: 'Code',         width: '100px', sortable: true },
          { key: 'serviceName', header: 'Service Name', width: '200px', sortable: true },
          { key: 'categoryName',header: 'Category',     width: '150px', sortable: true },
          { key: 'uom',         header: 'UOM',          width: '80px',  sortable: true },
          {
            key: 'statusName', header: 'Status', width: '100px',
            render: (_value: string, record: ServiceMasterRecord) => (
              <span
                className="badge"
                style={record.statusColor ? { background: record.statusColor, color: '#fff' } : undefined}
              >
                {record.statusName || record.statusCode || '—'}
              </span>
            )
          },
        ]}
        data={apiData}
        filters={[
          { key: 'statusName', label: 'Status', type: 'text' }
        ]}
        rowKey="id"
        enableRecentlyUsed
        recentlyUsedKey="sample-master-search"
        searchPlaceholder="Search by code or name... (F2 to focus)"
      />
    </div>
  );
};

export default SampleMasterScreen;
